/**
 * PublicDashboardPage — customer-facing dashboard (/d/:token).
 * No auth. Shows THIS customer's bookings for today + the next 7 days with a
 * live indicator on active trips. Data-minimized (no other customers' data).
 * Polls every ~20 s, backs off to 60 s when the tab is hidden.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, MapPin, Clock, XCircle, CalendarDays, Bus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePublicLanguage } from '@/components/public/usePublicLanguage';
import PublicLanguageToggle from '@/components/public/PublicLanguageToggle';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (d?: string | null, lang = 'no') => {
  if (!d) return d || '';
  try { return new Date(d + 'T00:00:00').toLocaleDateString(lang === 'en' ? 'en-GB' : 'nb-NO', { weekday: 'short', day: 'numeric', month: 'short' }); }
  catch { return d; }
};

const STATUS_KEYS: Record<string, [string, string]> = {
  pending: ['public.status.pending', 'Venter'], confirmed: ['public.status.confirmed', 'Bekreftet'], betinget_taxi: ['public.status.betingetTaxi', 'Betinget taxi'],
  cancelled: ['public.status.cancelled', 'Kansellert'], kansellert: ['public.status.cancelled', 'Kansellert'], completed: ['public.status.completed', 'Fullført'],
};

export default function PublicDashboardPage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<any>(null);
  const { lang, setPublicLanguage } = usePublicLanguage(data?.preferred_language);
  const toggle = <PublicLanguageToggle lang={lang} onChange={setPublicLanguage} />;
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const timerRef = useRef<any>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/public/customer-dashboard/${token}`);
      if (res.status === 404) { setNotFound(true); return; }
      if (!res.ok) return;
      setData(await res.json());
    } catch { /* keep last data */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      await load();
      if (cancelled) return;
      const delay = document.visibilityState === 'hidden' ? 60000 : 20000;
      timerRef.current = setTimeout(tick, delay);
    };
    tick();
    const onVis = () => { if (document.visibilityState === 'visible') { clearTimeout(timerRef.current); tick(); } };
    document.addEventListener('visibilitychange', onVis);
    return () => { cancelled = true; clearTimeout(timerRef.current); document.removeEventListener('visibilitychange', onVis); };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" data-testid="dashboard-loading" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center relative" data-testid="dashboard-not-found">
          <div className="absolute right-3 top-3">{toggle}</div>
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-800 mb-2">{t('publicDashboardPage.lenkenErIkkeGyldig', 'Lenken er ikke gyldig')}</h1>
          <p className="text-sm text-slate-500">{t('publicDashboardPage.lenkenKanVaereFeilEller', 'Lenken kan være feil eller deaktivert. Kontakt oss hvis du mener dette er en feil.')}</p>
        </div>
      </div>
    );
  }

  const company = data?.company || {};
  const bookings = data?.bookings || [];

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" data-testid="dashboard-page">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-5 relative">
          <div className="absolute right-0 top-0">{toggle}</div>
          {company.logo && <img src={company.logo} alt={company.name || ''} className="h-10 mx-auto mb-2 object-contain" />}
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider" data-testid="dashboard-company">{company.name}</p>
          <h1 className="text-2xl font-bold text-slate-800 mt-1">{t('publicDashboardPage.dineKommendeTurer', 'Dine kommende turer')}</h1>
          {data?.range && (
            <p className="text-xs text-slate-400 mt-1">{fmtDate(data.range.from, lang)} – {fmtDate(data.range.to, lang)}</p>
          )}
        </div>

        {bookings.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center" data-testid="dashboard-empty">
            <CalendarDays className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">{t('publicDashboardPage.ingenKommendeTurerDeNeste', 'Ingen kommende turer de neste 7 dagene.')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {bookings.map((b: any, i: number) => {
              const cancelled = ['cancelled', 'kansellert'].includes((b.status || '').toLowerCase());
              return (
                <div key={i} className={`bg-white rounded-xl shadow-sm border border-slate-100 p-4 ${cancelled ? 'opacity-60' : ''}`} data-testid={`dashboard-booking-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
                      <CalendarDays className="w-4 h-4 text-slate-400" />
                      {fmtDate(b.date, lang)}{b.time ? ` · ${t('common.atTime', 'kl.')} ${b.time}` : ''}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{STATUS_KEYS[b.status] ? t(...STATUS_KEYS[b.status]) : (b.status || '—')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-800">
                    <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span className="text-sm truncate">{b.from}{b.to ? ` → ${b.to}` : ''}</span>
                  </div>
                  {b.live && (
                    <div className="mt-2 inline-flex items-center gap-2 text-xs font-semibold text-emerald-700" data-testid={`dashboard-live-${i}`}>
                      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                      {b.live.status === 'underway' ? t('public.busUnderway', 'Bussen er underveis') : b.live.status === 'completed' ? t('publicDashboardPage.fullfort', 'Fullført') : t('public.ready', 'Klar')}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {(company.phone || company.email) && (
          <p className="text-center text-xs text-slate-400 mt-4">
            {t('public.questions', 'Spørsmål?')} {company.phone && `${t('public.call', 'Ring')} ${company.phone}`}{company.phone && company.email && ` ${t('common.or', 'eller')} `}
            {company.email && `${t('public.sendEmailTo', 'send e-post til')} ${company.email}`}
          </p>
        )}
      </div>
    </div>
  );
}
